import React, { useEffect, useState } from 'react';
import CuadroDatos from '../../UI/CuadroDatos.js';
import useGetListaURL from '../../../hooks/useGetListaURL.js';
import * as CondicionLocalia from '../../constantes/CondicionLocalia.js';
import * as DataPartidos from '../../data/DataPartidos.js';
import { getCategoriaIdRival } from '../../funciones/FuncionesHistoriales.js';
import styles from './HistorialRival.module.css';
import LinkPlus from '../../UI/LinkPlus.js';

const HistorialRival = (props) => {
  const club = props.club;
  const rival = props.rival;
  const filtro = props.filtro;
  const [totales, setTotales] = useState([]);
  const [esMobile, setEsMobile] = useState(window.innerWidth < 1025);
  const { isCargando, erroresGetLista, fetchData, resetErroresGetLista } = useGetListaURL();

  React.useEffect(() => {
    const handleResize = () => {
      setEsMobile(window.innerWidth < 1025);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const idClub = club && club.id;
  const tipoRival = rival && rival.tipo.id;
  const idRival = rival && rival.objeto && rival.objeto.id;
  const idCategoria = getCategoriaIdRival(rival);

  useEffect(() => {
    resetErroresGetLista();
  }, [resetErroresGetLista]);

  useEffect(() => {
    if (idClub) {
      const idBusqueda = idRival ? idRival : idClub;
      const path = DataPartidos.PathClubTipoRival(idClub, tipoRival);
      if (path) {
        const url = DataPartidos.URLTotalesPartidos(path, idBusqueda, filtro, idCategoria);
        fetchData(url, setTotales);
      }
    } else {
      setTotales([]);
    }
  }, [fetchData, idClub, filtro, tipoRival, idRival, idCategoria]);

  const tituloCondicion = (condicion) => {
    switch (condicion) {
      case CondicionLocalia.TODOS: {
        return 'Total';
      }
      case CondicionLocalia.LOCAL: {
        return 'Local';
      }
      case CondicionLocalia.VISITANTE: {
        return 'Visitante';
      }
      case CondicionLocalia.NEUTRAL: {
        return 'Neutral';
      }
      default: {
        return '';
      }
    }
  };

  // Agrupar observaciones por idPartido para mobile
  const agruparObservacionesMobile = () => {
    const obsMap = new Map(); // Map de idPartido -> { observaciones: string, condiciones: Set<string> }

    totales.forEach((item) => {
      if (item.observaciones && item.observaciones.length > 0) {
        const condicionNombre = tituloCondicion(item.condicion);
        item.observaciones.forEach((obs) => {
          const key = obs.idPartido;
          if (!obsMap.has(key)) {
            obsMap.set(key, {
              idPartido: obs.idPartido,
              observaciones: obs.observaciones,
              condiciones: new Set(),
            });
          }
          obsMap.get(key).condiciones.add(condicionNombre);
        });
      }
    });

    return Array.from(obsMap.values());
  };

  return (
    <CuadroDatos titulo="Totales" isCargando={isCargando} errores={erroresGetLista}>
      {!isCargando && !erroresGetLista && (
        <React.Fragment>
          {totales.length > 0 ? (
            esMobile ? (
              // Formato mobile: Tabla única compacta con todas las condiciones
              <div className={`${styles.containerMobile}`}>
                <table className={`${styles.tablaCompactaUnificada}`}>
                  <thead>
                    <tr>
                      <th>Condición</th>
                      <th>PJ</th>
                      <th>PG</th>
                      <th>PE</th>
                      <th>PP</th>
                      <th>GF</th>
                      <th>GC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {totales.map((item, index) => (
                      <tr key={index}>
                        <td className={`${styles.condicionNombre}`}>
                          {tituloCondicion(item.condicion)}
                        </td>
                        <td>{item.partidosJugados}</td>
                        <td>{item.partidosGanadosPropios}</td>
                        <td>{item.partidosEmpatados}</td>
                        <td>{item.partidosGanadosRival}</td>
                        <td>{item.golesPropios}</td>
                        <td>{item.golesRival}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {totales.length > 0 &&
                  totales.some((item) => item.observaciones && item.observaciones.length > 0) && (
                    <div className={`${styles.observacionesMobile}`}>
                      {agruparObservacionesMobile().map((obs) => (
                        <div key={obs.idPartido} className={`${styles.obsItem}`}>
                          <LinkPlus to={`/partido/${obs.idPartido}`} adaptativo={true}>
                            (*)
                          </LinkPlus>
                          <span className={`${styles.obsCondiciones}`}>
                            [{Array.from(obs.condiciones).join(', ')}]
                          </span>
                          {' ' + obs.observaciones}
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            ) : (
              // Formato desktop: Tabla original
              <table className={`${styles.tablaTotales}`}>
                <thead>
                  <tr>
                    <th className={`${styles.columnaTitulo}`}></th>
                    <th className={`${styles.columnaValor}`}></th>
                  </tr>
                </thead>
                <tbody>
                  {totales.map((item, index) => (
                    <React.Fragment key={index}>
                      <tr className={`${styles.filaCondicion}`}>
                        <td className={`${styles.condicion}`}>{tituloCondicion(item.condicion)}</td>
                        <td className={`${styles.totalPartidos}`}>{item.partidosJugados}</td>
                      </tr>
                      <tr key={index + 'PG'}>
                        <td className={`${styles.filaTitulo}`}>{club.nombre}</td>
                        <td className={`${styles.filaValor}`}>{item.partidosGanadosPropios}</td>
                      </tr>
                      <tr key={index + 'PE'}>
                        <td className={`${styles.filaTitulo}`}>Empates</td>
                        <td className={`${styles.filaValor}`}>{item.partidosEmpatados}</td>
                      </tr>
                      <tr key={index + 'PP'}>
                        <td className={`${styles.filaTitulo}`}>
                          {rival.objeto && rival.objeto.nombre}
                        </td>
                        <td className={`${styles.filaValor}`}>{item.partidosGanadosRival}</td>
                      </tr>
                      <tr key={index + 'GF'}>
                        <td className={`${styles.filaTitulo}`}>Goles {club.nombre}</td>
                        <td className={`${styles.filaValor}`}>{item.golesPropios}</td>
                      </tr>
                      <tr key={index + 'GC'}>
                        <td className={`${styles.filaTitulo}`}>
                          Goles {rival.objeto && rival.objeto.nombre}
                        </td>
                        <td className={`${styles.filaValor}`}>{item.golesRival}</td>
                      </tr>
                      {item.observaciones && item.observaciones.length > 0 && (
                        <React.Fragment>
                          {item.observaciones.map((obs, obsIndex) => (
                            <tr
                              key={index + 'Obs' + obsIndex}
                              className={`${styles.filaObservaciones}`}
                            >
                              <td colSpan="2">
                                <LinkPlus to={`/partido/${obs.idPartido}`} adaptativo={true}>
                                  (*)
                                </LinkPlus>
                                {' ' + obs.observaciones}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )
          ) : (
            <span className={`${styles.noPartidos}`}>No se enfrentaron</span>
          )}
        </React.Fragment>
      )}
    </CuadroDatos>
  );
};

export default HistorialRival;
